import axios from "axios";

import {
  CLIENT_CONTEXT_HEADER,
  clientContextHeaderValue,
} from "@/lib/clientContext";
import { authzVersionFromHeaders, observeAuthzVersion } from "@/lib/authzVersion";

// ============================================================================
// CONFIGURAÇÃO BASE DA API
// ============================================================================

// Descobre automaticamente a URL do backend.
//
// Mesma origem é o padrão (produção e preview da Emergent repassam /api no
// mesmo domínio). REACT_APP_BACKEND_URL só vale para desenvolvimento local.
//
// Guarda de publicação: se a variável apontar para um host de loopback
// (localhost/127.0.0.1) mas a página veio de outro host (domínio público),
// o valor é um resquício de ambiente de desenvolvimento — no navegador do
// usuário aquele host é a máquina DELE e o CORS/Private Network Access
// bloqueia a chamada (incidente de 2026-10: login e sessão nunca subiam).
// Nesse caso usa-se a mesma origem, como em produção.
function resolveBackendUrl() {
  const configured = process.env.REACT_APP_BACKEND_URL?.trim();
  if (!configured) return window.location.origin;

  const LOOPBACK_HOSTS = ["localhost", "127.0.0.1", "[::1]", "::1"];
  const pageHost = window.location.hostname;
  try {
    const url = new URL(configured, window.location.origin);
    const isLoopback = LOOPBACK_HOSTS.includes(url.hostname);
    const pageIsLoopback = LOOPBACK_HOSTS.includes(pageHost);
    if (isLoopback && !pageIsLoopback) {
      console.warn(
        "[api] REACT_APP_BACKEND_URL aponta para loopback mas a página foi servida de outro host; usando a mesma origem.",
      );
      return window.location.origin;
    }
    return url.origin;
  } catch {
    return window.location.origin;
  }
}

const BACKEND_URL = resolveBackendUrl();

export const API = `${BACKEND_URL}/api`;

console.log("BACKEND_URL =", BACKEND_URL);
console.log("API =", API);

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// ============================================================================
// FILTRO GLOBAL DE CLIENTES — injetado em TODAS as requisições
// ============================================================================
// A seleção global vive no ClientContext; aqui ela é enviada como cabeçalho
// para que o backend estreite as consultas operacionais (interseção com o
// escopo autorizado). Sem seleção ativa, o cabeçalho NÃO é enviado e o
// comportamento anterior é preservado (compatibilidade total).
//
// O backend SEMPRE revalida a seleção contra o escopo efetivo: o cabeçalho
// nunca concede acesso, apenas restringe.
// ============================================================================
api.interceptors.request.use((config) => {
  try {
    const value = clientContextHeaderValue();
    if (value !== null) {
      config.headers = config.headers || {};
      // axios 1.x usa AxiosHeaders: a forma canônica é `.set()`. O fallback
      // cobre versões/objetos simples.
      if (typeof config.headers.set === "function") {
        config.headers.set(CLIENT_CONTEXT_HEADER, value);
      } else {
        config.headers[CLIENT_CONTEXT_HEADER] = value;
      }
    }
  } catch {
    // Nunca impedir uma requisição por falha na leitura do contexto.
  }
  return config;
});

// ============================================================================
// VERSÃO DA AUTORIZAÇÃO — permissões alteradas valem sem novo login
// ============================================================================
// Toda resposta autenticada traz X-Astral-Authz-Version; se diferir da versão
// carregada, a interface recarrega permissões, menu e capacidades
// (lib/authzVersion.js). Inclui respostas de erro: um 403 depois de uma
// revogação já traz a versão nova.
const observeResponseVersion = (response) => {
  try {
    observeAuthzVersion(authzVersionFromHeaders(response?.headers));
  } catch {
    // Nunca interferir na resposta por falha na leitura do cabeçalho.
  }
};

api.interceptors.response.use(
  (response) => {
    observeResponseVersion(response);
    return response;
  },
  (error) => {
    observeResponseVersion(error?.response);
    return Promise.reject(error);
  },
);

// ============================================================================
// TRATAMENTO DE ERROS
// ============================================================================

export function formatApiErrorDetail(detail) {
  if (detail == null) {
    return "Algo deu errado. Tente novamente.";
  }

  if (typeof detail === "string") {
    return detail;
  }

  if (typeof detail === "number" || typeof detail === "boolean") {
    return String(detail);
  }

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (item == null) {
          return "";
        }

        if (typeof item === "string") {
          return item;
        }

        if (
          item &&
          typeof item.message === "string" &&
          item.message.trim()
        ) {
          return item.message;
        }

        if (
          item &&
          typeof item.msg === "string" &&
          item.msg.trim()
        ) {
          return item.msg;
        }

        if (
          item &&
          typeof item.detail === "string" &&
          item.detail.trim()
        ) {
          return item.detail;
        }

        if (
          item &&
          typeof item.reason === "string" &&
          item.reason.trim()
        ) {
          return item.reason;
        }

        try {
          return JSON.stringify(item);
        } catch {
          return "";
        }
      })
      .filter(Boolean);

    return messages.length
      ? messages.join(" • ")
      : "Algo deu errado. Tente novamente.";
  }

  if (typeof detail === "object") {
    if (
      typeof detail.message === "string" &&
      detail.message.trim()
    ) {
      return detail.message;
    }

    if (
      typeof detail.msg === "string" &&
      detail.msg.trim()
    ) {
      return detail.msg;
    }

    if (
      typeof detail.detail === "string" &&
      detail.detail.trim()
    ) {
      return detail.detail;
    }

    if (
      typeof detail.reason === "string" &&
      detail.reason.trim()
    ) {
      return detail.reason;
    }

    if (
      Array.isArray(detail.reasons) &&
      detail.reasons.length
    ) {
      const messages = detail.reasons
        .map((item) => {
          if (typeof item === "string") {
            return item;
          }

          if (
            item &&
            typeof item.message === "string" &&
            item.message.trim()
          ) {
            return item.message;
          }

          if (
            item &&
            typeof item.msg === "string" &&
            item.msg.trim()
          ) {
            return item.msg;
          }

          if (
            item &&
            typeof item.detail === "string" &&
            item.detail.trim()
          ) {
            return item.detail;
          }

          if (
            item &&
            typeof item.reason === "string" &&
            item.reason.trim()
          ) {
            return item.reason;
          }

          return "";
        })
        .filter(Boolean);

      if (messages.length) {
        return messages.join(" • ");
      }
    }

    if (
      typeof detail.error === "string" &&
      detail.error.trim()
    ) {
      return detail.error;
    }

    try {
      const serialized = JSON.stringify(detail);

      if (
        serialized &&
        serialized !== "{}"
      ) {
        return serialized;
      }
    } catch {
      // Fallback abaixo.
    }
  }

  return "Algo deu errado. Tente novamente.";
}

// ============================================================================
// IMAGENS
// ============================================================================

export const compressImage = (
  file,
  maxW = 1280,
  quality = 0.72
) =>
  new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error("Arquivo de imagem não informado."));
      return;
    }

    const img = new Image();

    img.onload = () => {
      try {
        const scale = Math.min(
          1,
          maxW / img.width
        );

        const canvas =
          document.createElement("canvas");

        canvas.width = Math.round(
          img.width * scale
        );

        canvas.height = Math.round(
          img.height * scale
        );

        const context = canvas.getContext("2d");

        if (!context) {
          reject(
            new Error(
              "Não foi possível criar o contexto da imagem."
            )
          );
          return;
        }

        context.drawImage(
          img,
          0,
          0,
          canvas.width,
          canvas.height
        );

        resolve(
          canvas.toDataURL(
            "image/jpeg",
            quality
          )
        );

        URL.revokeObjectURL(img.src);
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(img.src);

      reject(
        new Error(
          "Não foi possível carregar a imagem."
        )
      );
    };

    img.src = URL.createObjectURL(file);
  });

// ============================================================================
// DOWNLOAD GENÉRICO
// ============================================================================

export async function downloadFile(
  path,
  filename
) {
  const { data } = await api.get(path, {
    responseType: "blob",
  });

  const url = URL.createObjectURL(data);

  const a = document.createElement("a");

  a.href = url;
  a.download = filename;

  document.body.appendChild(a);

  a.click();

  a.remove();

  URL.revokeObjectURL(url);
}

// ============================================================================
// SALAS ELÉTRICAS / CABINES REMOTAS
// ============================================================================
//
// IMPORTANTE:
//
// Não criamos uma API paralela.
//
// Tudo usa o mesmo objeto `api` existente acima.
//
// Tipos:
//   sala_eletrica
//   cabine_remota
//
// Registros antigos sem room_type continuam sendo tratados pelo backend
// como sala_eletrica.
// ============================================================================

export const ELECTRICAL_ROOM_TYPES = {
  SALA_ELETRICA: "sala_eletrica",
  CABINE_REMOTA: "cabine_remota",
};

// ============================================================================
// LIMPEZA DE PARÂMETROS
// ============================================================================

function cleanElectricalRoomParams(
  params = {}
) {
  return Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) =>
        value !== undefined &&
        value !== null &&
        value !== ""
    )
  );
}

// ============================================================================
// SALAS ELÉTRICAS
// ============================================================================

/**
 * Lista Salas Elétricas e/ou Cabines Remotas.
 *
 * Exemplos:
 *
 * getElectricalRooms()
 *
 * getElectricalRooms({
 *   client_id: "cliente-1",
 *   unit_id: "unidade-1",
 *   room_type: "sala_eletrica"
 * })
 *
 * getElectricalRooms({
 *   room_type: "cabine_remota"
 * })
 */
export async function getElectricalRooms(
  filters = {}
) {
  const params =
    cleanElectricalRoomParams(filters);

  const { data } = await api.get(
    "/electrical-rooms",
    {
      params,
    }
  );

  if (Array.isArray(data)) {
    return data;
  }

  return (
    data?.items ||
    data?.rooms ||
    []
  );
}

// ============================================================================
// CABINES REMOTAS
// ============================================================================

/**
 * Lista exclusivamente as Cabines Remotas.
 *
 * O backend determina isso automaticamente por:
 *
 * room_type = "cabine_remota"
 */
export async function getRemoteCabins(
  filters = {}
) {
  const params =
    cleanElectricalRoomParams(filters);

  const { data } = await api.get(
    "/electrical-rooms/remote-cabins",
    {
      params,
    }
  );

  if (Array.isArray(data)) {
    return data;
  }

  return (
    data?.items ||
    data?.rooms ||
    []
  );
}

// ============================================================================
// MONITORAMENTOS / HISTÓRICO
// ============================================================================

/**
 * Busca o histórico de monitoramentos.
 *
 * Filtros disponíveis:
 *
 * room_id
 * client_id
 * unit_id
 * room_type
 * days
 * limit
 *
 * period:
 *   semana
 *   mes
 *   ano
 *
 * year
 * month
 * week
 * start_date
 * end_date
 */
export async function getElectricalRoomMonitorings(
  filters = {}
) {
  const params =
    cleanElectricalRoomParams(filters);

  const { data } = await api.get(
    "/electrical-rooms/monitorings",
    {
      params,
    }
  );

  if (Array.isArray(data)) {
    return {
      items: data,
      total: data.length,
      page: 1,
      pages: data.length ? 1 : 0,
    };
  }

  return {
    items: data?.items || data?.monitorings || [],
    total: Number(data?.total || (data?.items || []).length || 0),
    page: Number(data?.page || 1),
    page_size: Number(data?.page_size || 50),
    pages: Number(data?.pages || (data?.total ? 1 : 0)),
  };
}

// ============================================================================
// DASHBOARD
// ============================================================================

/**
 * Dashboard das Salas Elétricas ou Cabines Remotas.
 */
export async function getElectricalRoomDashboard(
  filters = {}
) {
  const params =
    cleanElectricalRoomParams(filters);

  const { data } = await api.get(
    "/electrical-rooms/dashboard",
    {
      params,
    }
  );

  return data || null;
}

// ============================================================================
// DETALHAMENTO / HISTÓRICO DE UMA SALA
// ============================================================================

/**
 * Busca o histórico detalhado de uma Sala Elétrica
 * ou Cabine Remota específica.
 */
export async function getElectricalRoomHistory(
  roomId,
  filters = {}
) {
  if (!roomId) {
    throw new Error(
      "roomId é obrigatório para consultar o histórico."
    );
  }

  const params =
    cleanElectricalRoomParams(filters);

  const { data } = await api.get(
    `/electrical-rooms/${encodeURIComponent(
      roomId
    )}`,
    {
      params,
    }
  );

  return data;
}

// ============================================================================
// CRIAÇÃO DE MONITORAMENTO
// ============================================================================

/**
 * Cria um novo monitoramento.
 *
 * O payload deve continuar sendo o mesmo utilizado
 * atualmente pelo ElectricInspectionFlow.
 */
export async function createElectricalRoomMonitoring(
  roomId,
  body
) {
  if (!roomId) {
    throw new Error(
      "roomId é obrigatório para criar o monitoramento."
    );
  }

  const { data } = await api.post(
    `/electrical-rooms/${encodeURIComponent(
      roomId
    )}/monitorings`,
    body
  );

  return data;
}

// ============================================================================
// CHECKLIST
// ============================================================================

/**
 * Busca o template atual do checklist.
 */
export async function getElectricalRoomChecklistTemplate() {
  const { data } = await api.get(
    "/electrical-rooms/checklist-template"
  );

  return data;
}

// ============================================================================
// ESPÉCIES DE ROEDORES
// ============================================================================

export async function getElectricalRoomRodentSpecies(
  includeInactive = false
) {
  const { data } = await api.get(
    "/electrical-rooms/rodent-species",
    {
      params: {
        include_inactive:
          includeInactive,
      },
    }
  );

  if (Array.isArray(data)) {
    return data;
  }

  return (
    data?.items ||
    data?.species ||
    []
  );
}

// ============================================================================
// TIPOS DE VESTÍGIOS
// ============================================================================

export async function getElectricalRoomVestigioTypes(
  includeInactive = false
) {
  const { data } = await api.get(
    "/electrical-rooms/vestigio-types",
    {
      params: {
        include_inactive:
          includeInactive,
      },
    }
  );

  if (Array.isArray(data)) {
    return data;
  }

  return (
    data?.items ||
    data?.types ||
    []
  );
}

// ============================================================================
// URL DO PDF
// ============================================================================

/**
 * Monta a URL do relatório PDF.
 *
 * Exemplo:
 *
 * getElectricalRoomReportUrl({
 *   room_type: "sala_eletrica",
 *   period: "mes",
 *   year: 2026,
 *   month: 8
 * })
 *
 * Resultado:
 *
 * /api/electrical-rooms/reports/pdf?room_type=sala_eletrica...
 */
export function getElectricalRoomReportUrl(
  filters = {}
) {
  const params =
    new URLSearchParams(
      cleanElectricalRoomParams(
        filters
      )
    );

  return `${API}/electrical-rooms/reports/pdf?${params.toString()}`;
}

// ============================================================================
// DOWNLOAD DO PDF
// ============================================================================

/**
 * Baixa o PDF de Salas Elétricas ou Cabines Remotas.
 *
 * room_type:
 *
 * sala_eletrica
 *     -> relatorio_salas_eletricas.pdf
 *
 * cabine_remota
 *     -> relatorio_cabines_remotas.pdf
 */
export async function downloadElectricalRoomReport(
  filters = {},
  filename
) {
  const params =
    cleanElectricalRoomParams(
      filters
    );

  const roomType =
    params.room_type ||
    ELECTRICAL_ROOM_TYPES.SALA_ELETRICA;

  const defaultFilename =
    roomType ===
    ELECTRICAL_ROOM_TYPES.CABINE_REMOTA
      ? "relatorio_cabines_remotas.pdf"
      : "relatorio_salas_eletricas.pdf";

  const { data } = await api.get(
    "/electrical-rooms/reports/pdf",
    {
      params,
      responseType: "blob",
    }
  );

  const blobUrl =
    URL.createObjectURL(data);

  const anchor =
    document.createElement("a");

  anchor.href = blobUrl;

  anchor.download =
    filename || defaultFilename;

  document.body.appendChild(anchor);

  anchor.click();

  anchor.remove();

  URL.revokeObjectURL(blobUrl);

  return data;
}

// ============================================================================
// MIGRAÇÃO DE CADASTROS ANTIGOS
// ============================================================================
//
// Registros antigos que ainda não possuem room_type
// passam a ser classificados como sala_eletrica.
//
// Essa função é administrativa.
// ============================================================================

export async function migrateElectricalRoomTypes() {
  const { data } = await api.post(
    "/electric-rooms/migrate-room-types"
  );

  return data;
}